/**
 * Device code flow modal for GitHub Copilot authentication
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { useToast } from "@/hooks/use-toast.ts";
import { CopilotClient } from "@/lib/ai/copilot.ts";
import { getJWTExpiration } from "@/lib/auth/jwt.ts";
import type { AuthTokens } from "@/lib/auth/tokens.ts";
import type { DeviceCodeResponse } from "@/lib/ai/types.ts";

interface CopilotAuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (tokens: AuthTokens) => void;
}

export function CopilotAuthModal({
  open,
  onOpenChange,
  onSuccess,
}: CopilotAuthModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [deviceCode, setDeviceCode] = useState<DeviceCodeResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  /**
   * Start device code flow when modal opens
   */
  useEffect(() => {
    if (open && !deviceCode) {
      startDeviceFlow();
    }
  }, [open]);

  /**
   * Step 1: Request device code
   */
  const startDeviceFlow = async () => {
    setIsLoading(true);
    try {
      const response = await CopilotClient.requestDeviceCode();
      setDeviceCode(response);

      // Auto-start polling
      pollForToken();
    } catch (error) {
      toast({
        title: t("auth.error"),
        description: error instanceof Error ? error.message : t("auth.unknownError"),
        variant: "destructive",
      });
      onOpenChange(false);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Step 2: Poll for access token
   */
  const pollForToken = async () => {
    try {
      const tokenResponse = await CopilotClient.completeDeviceFlow(
        (seconds) => setElapsed(seconds),
      );

      // Create auth tokens
      const tokens: AuthTokens = {
        provider: "copilot",
        accessToken: tokenResponse.access_token,
        refreshToken: tokenResponse.refresh_token,
        expiresAt: tokenResponse.expires_in
          ? Date.now() + tokenResponse.expires_in * 1000
          : getJWTExpiration(tokenResponse.access_token) ?? undefined,
      };

      onSuccess(tokens);
      onOpenChange(false);

      toast({
        title: t("auth.success"),
        description: t("auth.copilotConnected"),
      });
    } catch (error) {
      toast({
        title: t("auth.error"),
        description: error instanceof Error ? error.message : t("auth.unknownError"),
        variant: "destructive",
      });
      onOpenChange(false);
    }
  };

  /**
   * Copy user code to clipboard
   */
  const copyUserCode = () => {
    if (deviceCode?.user_code) {
      navigator.clipboard.writeText(deviceCode.user_code);
      toast({
        title: t("auth.copied"),
        description: t("auth.codeCopied"),
      });
    }
  };

  /**
   * Open verification URL in new tab
   */
  const openVerificationUrl = () => {
    if (deviceCode?.verification_uri) {
      globalThis.open(deviceCode.verification_uri, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("auth.copilotAuth")}</DialogTitle>
          <DialogDescription>
            {t("auth.copilotAuthDescription")}
          </DialogDescription>
        </DialogHeader>

        {isLoading
          ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          )
          : deviceCode
          ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t("auth.userCode")}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={deviceCode.user_code}
                    readOnly
                    className="font-mono text-lg"
                  />
                  <Button onClick={copyUserCode} variant="outline">
                    {t("common.copy")}
                  </Button>
                </div>
              </div>

              <Button
                onClick={openVerificationUrl}
                className="w-full"
                size="lg"
              >
                {t("auth.openGitHub")}
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                {t("auth.waitingForAuth")}
                {elapsed > 0 && ` (${Math.floor(elapsed)}s)`}
              </div>
            </div>
          )
          : (
            <div className="text-center text-sm text-muted-foreground py-4">
              {t("auth.loading")}
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}
